// UpdateMemberFlow — edit an existing grant (ORP-18889). v3 shipped this as TWO near-identical
// functions, `updateUser` and `updateInvite` (`UserPermissionModal.vue:530-560`), chosen by
// `formData.isInvite` — a flag that was never declared in the `reactive()` initialiser and is
// therefore `undefined` on the create path. The row union replaces the flag: narrowing IS the
// choice, so there is no third state to get wrong.
//
// It also retires v3's four-deep id guess. `updateUser` addressed
// `formData.obj.id ?? user_id ?? get_account?.id ?? account_permissions?.id` (`:531-532`), reaching
// into three containers whose ids live in different spaces (`models/member.types.ts`). Here the row
// carries exactly one branded id and the API method that accepts it is the one the URL family
// expects.
import { AccountMemberWritesApi } from '../api/AccountMemberWritesApi'
import { isPendingInviteRow, type MemberListRow } from '../models/member.types'
import type { MemberGrantPayload } from '../models/memberInvite.types'

import { reloadAccountContext } from './reloadAccountContext'
import type { MemberFlowOptions } from './RemoveMemberFlow'

import { useAccount } from '@/features/account'

export class UpdateMemberFlow {
  private readonly api: AccountMemberWritesApi

  constructor(options: MemberFlowOptions = {}) {
    this.api = options.api ?? new AccountMemberWritesApi()
  }

  async execute(
    row: MemberListRow,
    payload: MemberGrantPayload,
  ): Promise<{ refreshFailed: boolean }> {
    const { accountId } = useAccount()
    if (accountId.value === undefined) {
      throw new Error('UpdateMemberFlow: no account in scope for a grant update')
    }

    if (isPendingInviteRow(row)) {
      await this.api.updateInviteGrant(accountId.value, row.inviteId, payload)
    } else {
      await this.api.updateMemberGrant(accountId.value, row.userId, payload)
    }

    return { refreshFailed: !(await reloadAccountContext()) }
  }
}
